import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Database, Inbox, Lock, ShieldCheck } from 'lucide-react';
import type { CrmProjectsResponse } from '../../services/api';
import './CrmProjectBar.css';

/**
 * crm-per-project-scoping: slim glass pill pinned top-right inside the CRM frame.
 *
 * Rendered by CRMWorkspace ONLY when GET /api/crm/projects says `scoping: true`
 * (or `support_view: true` for the superadmin variant). It must never imply a
 * project filter the backend is not applying.
 */
export interface CrmProjectBarProps {
  info: CrmProjectsResponse;
  selectedProjectId?: string;
  bucket: 'project' | 'unassigned';
  onSelectProject: (projectId: string) => void;
  onSelectUnassigned: () => void;
}

const REASON_TEXT: Record<string, string> = {
  project_not_ready: 'Setting up…',
  identity_not_ready: 'Setting up…',
  no_project_access: 'No access',
  project_company_crm: 'Company CRM',
  member_not_migrated: 'Waiting for CRM upgrade',
  not_scoped: 'Not available yet',
  no_module: 'Not in your plan',
};

export const reasonLabel = (reason: string | null | undefined): string =>
  (reason && REASON_TEXT[reason]) || (reason ? 'Not available' : '');

const CrmProjectBar: React.FC<CrmProjectBarProps> = ({
  info, selectedProjectId, bucket, onSelectProject, onSelectUnassigned,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (info.support_view && !info.scoping) {
    return (
      <div className="crm-project-bar" data-testid="crm-project-bar">
        <div className="crm-project-bar__pill crm-project-bar__pill--static" title="Support view: all tenants (not filtered by project)">
          <ShieldCheck size={16} />
          <span className="crm-project-bar__label">Support view: all tenants (not filtered by project)</span>
        </div>
      </div>
    );
  }

  const current = info.projects.find(p => p.project_id === selectedProjectId);
  const isUnassigned = bucket === 'unassigned';
  const label = isUnassigned
    ? 'Unassigned records'
    : current ? current.name : 'Select a project';
  const readOnly = !isUnassigned && current?.crm_access === 'ro';

  return (
    <div className="crm-project-bar" ref={rootRef} data-testid="crm-project-bar">
      <button
        type="button"
        className="crm-project-bar__pill"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`CRM: ${label}${readOnly ? ' (read-only)' : ''}`}
        title={`CRM: ${label}`}
      >
        {isUnassigned ? <Inbox size={16} /> : <Database size={16} />}
        <span className="crm-project-bar__label">CRM: {label}</span>
        {readOnly && (
          <span className="crm-project-bar__ro">
            <Lock size={12} /> Read-only
          </span>
        )}
        <ChevronDown size={14} className="crm-project-bar__chevron" />
      </button>
      {open && (
        <ul className="crm-project-bar__menu" role="listbox">
          {info.projects.length === 0 && (
            <li className="crm-project-bar__empty">No projects with CRM access</li>
          )}
          {info.projects.map(p => {
            const disabled = !p.crm_ready;
            const selected = !isUnassigned && p.project_id === selectedProjectId;
            return (
              <li key={p.project_id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={disabled}
                  className={`crm-project-bar__item${selected ? ' is-selected' : ''}${p.is_archived ? ' is-archived' : ''}`}
                  onClick={() => { setOpen(false); onSelectProject(p.project_id); }}
                >
                  <span className="crm-project-bar__item-name">
                    {p.name}{p.is_archived ? ' (archived)' : ''}
                  </span>
                  <span className="crm-project-bar__item-meta">
                    {disabled
                      ? reasonLabel(p.reason)
                      : p.crm_access === 'ro'
                        ? <><Lock size={11} /> Read-only</>
                        : 'Read & write'}
                  </span>
                </button>
              </li>
            );
          })}
          {info.unassigned?.available && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={isUnassigned}
                className={`crm-project-bar__item${isUnassigned ? ' is-selected' : ''}`}
                onClick={() => { setOpen(false); onSelectUnassigned(); }}
              >
                <span className="crm-project-bar__item-name"><Inbox size={13} /> Unassigned records</span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CrmProjectBar;
